import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Sparkles, RefreshCw, Loader2, Copy, Check, X } from "lucide-react";

/**
 * TumorBoardBriefing — one-click AI briefing that mirrors how oncologists
 * present at Monday morning tumor board. Five short lines: TNM · trajectory ·
 * response · toxicities · next-decision. Cached per day by the backend.
 */
export default function TumorBoardBriefing() {
  const [briefing, setBriefing] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  async function generate(force = false) {
    setLoading(true);
    setOpen(true);
    try {
      const { data } = await api.get(
        `/specialties/oncology/tumor-board${force ? "?refresh=1" : ""}`
      );
      setBriefing(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to generate briefing");
      setOpen(false);
    } finally {
      setLoading(false);
    }
  }

  async function copyAll() {
    if (!briefing?.briefing) return;
    const b = briefing.briefing;
    const text = [b.tnm_line, b.trajectory_line, b.response_line, b.toxicities_line, b.decision_line]
      .filter(Boolean)
      .map((line, i) => `${i + 1}. ${line}`)
      .join("\n");
    try {
      // Modern clipboard API — requires HTTPS or same-origin permission
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Briefing copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: hidden textarea + document.execCommand("copy")
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        setCopied(true);
        toast.success("Briefing copied");
        setTimeout(() => setCopied(false), 2000);
      } catch {
        toast.error("Clipboard blocked — you can select the text and copy manually.");
      }
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => generate(false)}
        data-testid="tumor-board-generate-btn"
        className="w-full text-left ros-card hover:border-[#FBBF24]/60 transition-colors"
        style={{ background: "rgba(251,191,36,0.08)", borderColor: "rgba(251,191,36,0.35)" }}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl flex items-center justify-center"
                 style={{ background: "rgba(251,191,36,0.22)", border: "1px solid rgba(251,191,36,0.55)" }}>
              <FileText size={18} style={{ color: "#FBBF24" }} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#FBBF24" }}>
                  Tumor Board Briefing
                </span>
                <span className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">· Med Nexus AI</span>
              </div>
              <div className="font-heading text-base text-white mt-0.5">
                Generate a 5-line Monday-morning briefing →
              </div>
              <div className="text-xs text-[#A3ACA7] mt-1 max-w-xl">
                TNM · trajectory · response · toxicities · next decision — in the exact vernacular an attending would present.
              </div>
            </div>
          </div>
          <Sparkles size={16} style={{ color: "#FBBF24" }} />
        </div>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          data-testid="tumor-board-modal"
          onClick={() => setOpen(false)}
        >
          <div
            className="ros-card w-full max-w-2xl relative"
            onClick={(e) => e.stopPropagation()}
            style={{ borderColor: "rgba(251,191,36,0.55)" }}
          >
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="absolute top-3 right-3 text-[#A3ACA7] hover:text-white"
              data-testid="tumor-board-close"
              title="Close"
            >
              <X size={18} />
            </button>

            <div className="flex items-center gap-3 pr-8">
              <div className="w-10 h-10 rounded-2xl flex items-center justify-center"
                   style={{ background: "rgba(251,191,36,0.22)", border: "1px solid rgba(251,191,36,0.55)" }}>
                <FileText size={18} style={{ color: "#FBBF24" }} />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#FBBF24" }}>
                  Tumor Board Briefing
                </div>
                <div className="font-heading text-lg text-white">Monday morning · 60-second brief</div>
              </div>
            </div>

            <div className="mt-4">
              {loading ? (
                <div className="flex items-center gap-2 text-sm text-[#A3ACA7] py-8 justify-center"
                     data-testid="tumor-board-loading">
                  <Loader2 size={16} className="animate-spin" style={{ color: "#FBBF24" }} />
                  Composing the briefing…
                </div>
              ) : briefing?.briefing ? (
                <div className="space-y-3" data-testid="tumor-board-body">
                  {[
                    { key: "tnm_line",        label: "1. TNM & histology" },
                    { key: "trajectory_line", label: "2. Imaging trajectory" },
                    { key: "response_line",   label: "3. RECIST response" },
                    { key: "toxicities_line", label: "4. Regimen · cycle · toxicities" },
                    { key: "decision_line",   label: "5. Decision this week" },
                  ].map(({ key, label }) => (
                    briefing.briefing[key] ? (
                      <div key={key} data-testid={`tumor-board-${key}`}>
                        <div className="text-[10px] uppercase tracking-wider text-[#A3ACA7]">{label}</div>
                        <div className="text-sm text-[#E5E7EB] mt-1 leading-relaxed">
                          {briefing.briefing[key]}
                        </div>
                      </div>
                    ) : null
                  ))}
                </div>
              ) : (
                <div className="text-sm text-[#A3ACA7] py-6">No briefing available.</div>
              )}
            </div>

            <div className="mt-5 flex items-center justify-between gap-3">
              <div className="text-[10px] text-[#7A8480] flex items-center gap-1.5">
                <Sparkles size={10} style={{ color: "#FBBF24" }} />
                Med Nexus AI
                {briefing?.day_key && <span>· {briefing.day_key}</span>}
                {briefing?.cached && <span>· Cached today</span>}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={copyAll}
                  disabled={!briefing?.briefing || loading}
                  data-testid="tumor-board-copy"
                  className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#272E2B] text-[#A3ACA7] hover:text-white transition-colors disabled:opacity-50"
                >
                  {copied ? <Check size={12} /> : <Copy size={12} />}
                  {copied ? "Copied" : "Copy"}
                </button>
                <button
                  type="button"
                  onClick={() => generate(true)}
                  disabled={loading}
                  data-testid="tumor-board-refresh"
                  className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-full font-semibold text-black disabled:opacity-50"
                  style={{ background: "#FBBF24" }}
                >
                  <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
                  Regenerate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
